import {
  HfsMissionSettingsActionEnum,
  type MowerAreaSchedule,
  type MowerSchedule,
  type MowerScheduleSettingMetadata,
} from 'fleet-data-model';
import { DayName, dayNames, TaskList } from '@fleet-bffs/api-contract';
import { Task } from '~/data/Asset';
import Logger from '~/utils/logger/logger';
import type { LocalizedMission } from '~/domain/roboticmower-details/models/LocalizedMission';
import type { ScheduleTask } from '~/domain/assets/store/hfs-operations/reducer';
import { RoboticMowerScheduleWeekFields } from '~/domain/roboticmower-details/store/schedule/RoboticMowerScheduleWeek';
import { schedulesV2defaultState, TimeWiseTaskRecord, WeeklyScheduleRecord } from '../../store/schedule';
import { TaskWithDay } from './hooks';

/**
 * Helper function to deep clone objects using JSON parse/stringify
 * Use this instead of structuredClone for Redux state that may contain non-cloneable objects
 */
export const deepClone = <T>(obj: T): T => {
  // Using JSON.parse/stringify instead of structuredClone because Task objects are not cloneable
  // eslint-disable-next-line
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Helper function to filter tasks by mission ID
 */
export const filterTasksByMission = (
  tasks: Task[],
  missionId: number | null | undefined
): Task[] => {
  if (missionId === null || missionId === undefined) {
    return tasks;
  }
  return tasks.filter((task) => task.missionId === missionId);
};

/**
 * Helper function to convert time-wise task records to day-wise task records
 */
export const convertToDayWiseTasks = (
  timeWiseRecords: TimeWiseTaskRecord,
  missionId: string | number | null | undefined
): Record<DayName, Task[]> => {
  const dayWiseTaskRecords = {} as Record<DayName, Task[]>;

  for (const [slotKey, schedulesRecords] of Object.entries(timeWiseRecords)) {
    const { days, startTime, endTime, id } = schedulesRecords;

    if (days.length === 0) {
      continue;
    }

    for (const day of days) {
      if (!dayWiseTaskRecords[day]) {
        dayWiseTaskRecords[day] = [];
      }
      // Handle missionId: null/undefined/NaN should become 0
      const taskMissionId = missionId == null || Number.isNaN(missionId) ? 0 : +missionId;

      const task = {
        // Use the ID from the schedule record to maintain consistency across conversions
        id: id || `${slotKey}-${day}`,
        startTime,
        endTime,
        missionId: taskMissionId,
      };

      dayWiseTaskRecords[day].push(task);
    }
  }

  return dayWiseTaskRecords;
};

/**
 * Helper function to apply day-wise tasks to a schedule
 * For multi-mission mowers, only updates tasks for the specified missionId
 */
export const applyTaskListToSchedule = (
  schedule: WeeklyScheduleRecord | null | undefined,
  dayWiseTasks: Record<DayName, Task[]>,
  missionId?: number | null
): void => {
  if (!schedule) {
    return;
  }

  const allDays: DayName[] = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ];

  if (missionId === null || missionId === undefined || Number.isNaN(missionId)) {
    // Non-EPOS mode: Replace all tasks for each day with new tasks
    const daysWithNewTasks = new Set(Object.keys(dayWiseTasks));
    for (const day of daysWithNewTasks) {
      const dayName = day as DayName;
      if (schedule[dayName]) {
        schedule[dayName].taskList = dayWiseTasks[dayName] ?? [];
      }
    }
  } else {
    // EPOS mode: First remove all tasks for this mission from ALL days, then add new tasks
    for (const dayName of allDays) {
      if (!schedule[dayName]) {
        continue;
      }

      const existingTasks = schedule[dayName].taskList ?? [];
      // Remove tasks for this mission
      const tasksFromOtherMissions = existingTasks.filter((task) => task.missionId !== missionId);

      // Add new tasks for this mission (if any exist for this day)
      const newTasks = dayWiseTasks[dayName] ?? [];

      schedule[dayName].taskList = [...tasksFromOtherMissions, ...newTasks];
    }
  }
};

/**
 * Helper function to build weekly schedule from task list
 * For EPOS mowers with missions: filters tasks by selectedMissionId
 * For non-EPOS mowers: uses taskList directly without filtering
 */
export const buildWeeklySchedule = (taskList: TaskList): WeeklyScheduleRecord => {
  // Start with default state to get the correct structure
  const weeklySchedule = {
    ...schedulesV2defaultState.weeklyScheduleToDisplay,
  } as WeeklyScheduleRecord;

  // Update each day with tasks
  const days: DayName[] = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ];

  for (const day of days) {
    // If no mission ID (non-EPOS or single work area), use all tasks for the day
    // If mission ID exists (EPOS with multiple areas), filter by mission
    const dayTasks = taskList[day] || [];

    weeklySchedule[day] = {
      ...weeklySchedule[day],
      taskList: dayTasks,
    };
  }

  return weeklySchedule;
};

export const missionsToTaskList = (
  missions: Pick<LocalizedMission, 'taskList'>[] | undefined
): TaskList | null => {
  if (!missions || missions.length === 0) {
    return null;
  }

  const taskList: TaskList = {};
  for (const mission of missions) {
    const missionTaskList = mission.taskList;
    if (missionTaskList) {
      for (const [day, tasks] of Object.entries(missionTaskList)) {
        if (!taskList[day]) {
          taskList[day] = [];
        }
        taskList[day].push(...tasks);
      }
    }
  }

  // Return null if no tasks were found
  return Object.keys(taskList).length > 0 ? taskList : null;
};

/**
 * @description Group tasks by time slots
 * @param tasks List of tasks with day information
 * @returns Grouped tasks based on the time slots (key = first task's ID or time slot)
 */
const getGropedByTimeSlots = (tasks: TaskWithDay[]): TimeWiseTaskRecord => {
  const groupedByTimeSlots: TimeWiseTaskRecord = {};

  for (const task of tasks) {
    const timeSlotKey = `${task.startTime}-${task.endTime}`;

    // Find existing record with same time slot
    let existingRecordKey: string | undefined;
    for (const [key, record] of Object.entries(groupedByTimeSlots)) {
      if (record.startTime === task.startTime && record.endTime === task.endTime) {
        existingRecordKey = key;
        break;
      }
    }

    if (existingRecordKey) {
      // Add this day to existing record
      groupedByTimeSlots[existingRecordKey].days.push(task.day);
    } else {
      // Create new record - use task ID as key (preserves identity across conversions)
      const recordKey = task.id || `${timeSlotKey}`;
      groupedByTimeSlots[recordKey] = {
        startTime: task.startTime,
        endTime: task.endTime,
        id: task.id || timeSlotKey,
        days: [task.day],
      };
    }
  }

  return groupedByTimeSlots;
};

/**
 * @description Convert WeeklyScheduleRecord to TimeWiseTaskRecord for edit cards
 * @param weeklySchedule Weekly schedule record
 * @param selectedMissionId Optional mission ID to filter tasks
 * @returns Time-wise task record
 */
export const weeklyScheduleToTimeWise = (
  weeklySchedule: WeeklyScheduleRecord | undefined,
  selectedMissionId?: number | null
): TimeWiseTaskRecord => {
  if (!weeklySchedule) {
    return {};
  }

  const tasks: TaskWithDay[] = [];

  for (const [day, daySchedule] of Object.entries(weeklySchedule)) {
    const dayTasks = daySchedule.taskList || [];
    for (const task of dayTasks) {
      // Filter by mission if needed
      if (
        selectedMissionId !== null &&
        selectedMissionId !== undefined &&
        Number.isNaN(selectedMissionId) === false &&
        task.missionId !== selectedMissionId
      ) {
        continue;
      }
      tasks.push({
        ...task,
        day: day as DayName,
      });
    }
  }

  return getGropedByTimeSlots(tasks);
};

/**
 * Convert TimeWiseTaskRecord to WeeklyScheduleRecord
 */
export const convertTimeWiseToWeeklySchedule = (
  timeWiseTaskRecord: TimeWiseTaskRecord,
  selectedMissionId?: number | null
): WeeklyScheduleRecord => {
  const weeklySchedule =
    typeof structuredClone === 'function'
      ? structuredClone(schedulesV2defaultState.weeklyScheduleToDisplay!)
      : JSON.parse(JSON.stringify(schedulesV2defaultState.weeklyScheduleToDisplay!));

  for (const record of Object.values(timeWiseTaskRecord)) {
    for (const day of record.days) {
      if (!weeklySchedule[day].taskList) {
        weeklySchedule[day].taskList = [];
      }
      weeklySchedule[day].taskList.push({
        startTime: record.startTime,
        endTime: record.endTime,
        missionId: selectedMissionId ?? undefined,
        id: record.id + day,
      });
    }
  }

  return weeklySchedule;
};

export const mergeTimeWiseToWeeklySchedule = (
  originalState: WeeklyScheduleRecord,
  recordToMerge: TimeWiseTaskRecord,
  missionId?: number | null
) => {
  const subject = JSON.parse(JSON.stringify(recordToMerge));
  const origin = JSON.parse(JSON.stringify(originalState));
  const result = {};
  Object.keys(origin).forEach((day) => {
    const item = origin[day];
    const newTasks = Object.values(subject).filter((s) => s.days.includes(day));
    result[day] = {
      ...item,
      taskList: [
        ...item.taskList.filter((task) => task.missionId !== missionId),
        ...newTasks.map((newTask) => ({
          missionId,
          id: newTask.id,
          startTime: newTask.startTime,
          endTime: newTask.endTime,
        })),
      ],
    };
  });
  return result;
};

export const getMaxNumberOfTasksPerDay = (hfsActions: (MowerSchedule | MowerAreaSchedule)[]) => {
  const limits = hfsActions
    .map(
      (hfsAction) =>
        (hfsAction.settingData?.metadata as MowerScheduleSettingMetadata | undefined)
          ?.maxNumberOfTasksPerDay
    )
    .filter((value): value is number => value !== undefined);
  return Math.min(...limits);
};

export const getSmallestMaxNumberOfTasks = (hfsActions: (MowerSchedule | MowerAreaSchedule)[]) => {
  const limits = hfsActions
    .map(
      (hfsAction) =>
        (hfsAction.settingData?.metadata as MowerScheduleSettingMetadata | undefined)
          ?.maxNumberOfTasks
    )
    .filter((value): value is number => value !== undefined);
  return Math.min(...limits);
};

export const getMaxNumberOfAreasTasks = (hfsActions: (MowerSchedule | MowerAreaSchedule)[]) => {
  const areasCounts = new Map<string, number>();
  const maxNumbersOfTasks = new Map<string, number>();
  const areasActions = hfsActions.filter(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_SCHEDULE
  );

  if (areasActions.length === 0) {
    return 0;
  }

  areasActions.forEach((action) => {
    areasCounts.set(action.iprId, (areasCounts.get(action.iprId) ?? 0) + 1);
    maxNumbersOfTasks.set(
      action.iprId,
      (action.settingData?.metadata as MowerScheduleSettingMetadata | undefined)
        ?.maxNumberOfTasks ?? 1
    );
  });

  return Math.min(
    ...Array.from(areasCounts, ([iprId, areasCount]) => {
      const limit = maxNumbersOfTasks.get(iprId) ?? 1;
      Logger.log(
        `getMaxNumberOfAreasTasks: ${iprId} has ${areasCount} areas, limit ${limit}, overall ${
          limit / areasCount
        }`
      );
      return Math.floor(limit / areasCount);
    })
  );
};
export const calculateHighestNumberOfTasksPerDay = (tasks: ScheduleTask[]) => {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;
  return Math.max(0, ...days.map((day) => tasks.filter((task) => task.useOnDays[day]).length));
};

const generateRandomId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
export const formatScheduleRecordForAPI =
  (
    schedule: WeeklyScheduleRecord,
    assetIds: AssetId[],
    selectedMissionId?: number | string
  ): RoboticMowerScheduleWeekFields => {
    const result: RoboticMowerScheduleWeekFields = {
      assetIds,
      taskList: {
        MONDAY: [],
        TUESDAY: [],
        WEDNESDAY: [],
        THURSDAY: [],
        FRIDAY: [],
        SATURDAY: [],
        SUNDAY: [],
      },
      missionId: typeof selectedMissionId === 'number' ? selectedMissionId : null,
    };

    for (const [day, daySchedule] of Object.entries(schedule)) {
      let taskList = daySchedule?.taskList ?? [];

      // Filter by missionId if a valid one is provided
      if (selectedMissionId !== null && selectedMissionId !== undefined) {
        const missionIdNum =
          typeof selectedMissionId === 'string'
            ? Number.parseInt(selectedMissionId, 10)
            : selectedMissionId;
        if (!Number.isNaN(missionIdNum)) {
          taskList = taskList.filter((task) => task.missionId === missionIdNum);
        }
      }

      result.taskList[day] = taskList.map((task) => ({
        ...task,
        id: generateRandomId(),
      }));
    }

    return result;
  };

export const combineScheduleTasksToTasksList = (tasks: (ScheduleTask & { missionId: number })[]): TaskList => {
  const result: TaskList = {
    MONDAY: [],
    TUESDAY: [],
    WEDNESDAY: [],
    THURSDAY: [],
    FRIDAY: [],
    SATURDAY: [],
    SUNDAY: [],
  };
  dayNames.forEach((dayCode: DayName) => {
    const useOn = dayCode.toLowerCase();
    if (result[dayCode] !== undefined) {
      result[dayCode].push(
        ...tasks
          .filter(
            (task) =>
              task.useOnDays[useOn] === true &&
              task.start !== undefined &&
              task.duration !== undefined
          )
          .map((task) => ({
            missionId: task.missionId,
            id: task.taskId.toString(),
            startTime: task.start! / 60,
            endTime: (task.start! + task.duration!) / 60,
          }))
      );
    }
  });
  return result;
};


